/**
 * Public territory-name resolution for the Parcel Profile.
 *
 * Given the selected parcel, resolves the persistent Territory Identity's
 * display name (if the owner has named this territory) via the SAME bounded,
 * public discovery endpoint the map viewer uses. Read-only, no fabrication:
 * when no named identity is anchored to this parcel the state is "none" and the
 * UI simply shows nothing. Stale-safe (monotonic request id + AbortController).
 */
import { useEffect, useRef, useState } from "react";
import { batchTerritoryIdentities } from "../lib/brand-image-api";
import { normalizeOwnerIdentity } from "../domain/territory";

export interface TerritoryNameRef {
  id: number | string;
  owner: string;
}

export type TerritoryNameState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "none" }
  | { status: "ready"; name: string };

export function useTerritoryName(parcel: TerritoryNameRef | null): TerritoryNameState {
  const [state, setState] = useState<TerritoryNameState>({ status: "idle" });
  const requestIdRef = useRef(0);

  const id = parcel?.id ?? null;
  const owner = parcel?.owner ?? null;

  useEffect(() => {
    if (id == null || owner == null) {
      setState({ status: "idle" });
      return;
    }
    const myRequestId = ++requestIdRef.current;
    const controller = new AbortController();
    setState({ status: "loading" });

    (async () => {
      try {
        const pid = Number(id);
        if (!Number.isFinite(pid)) {
          setState({ status: "none" });
          return;
        }
        const identities = await batchTerritoryIdentities([pid], controller.signal);
        if (myRequestId !== requestIdRef.current) return; // superseded
        const ownerKey = normalizeOwnerIdentity(owner);
        const match = identities.find(
          (i) =>
            normalizeOwnerIdentity(i.owner) === ownerKey &&
            i.member_parcel_ids.includes(pid),
        );
        const name = match?.display_name?.trim();
        setState(name ? { status: "ready", name } : { status: "none" });
      } catch {
        if (controller.signal.aborted || myRequestId !== requestIdRef.current) return;
        setState({ status: "none" });
      }
    })();

    return () => controller.abort();
  }, [id, owner]);

  return state;
}
